import * as Sentry from '@sentry/vue'
import { AxiosError } from 'axios'
import type { ErrorEvent, EventHint, BrowserOptions } from '@sentry/vue'
import router from '@/router'

function shouldSkipErrorTracking(hint: EventHint): boolean {
  if (hint.originalException instanceof AxiosError) {
    const { response } = hint.originalException
    if (response?.status === 401)
      return true
  }

  return false
}

function getSentryConfig(): Partial<BrowserOptions> {
  const commonConfig = {
    dsn: import.meta.env.VITE_SENTRY_DSN || '',
    beforeSend: (event: ErrorEvent, hint: EventHint) => shouldSkipErrorTracking(hint) ? null : event,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
    ],
  }

  const tracePropagationTargets = import.meta.env.VITE_SENTRY_TRACE_PROPAGATION_TARGETS?.split(';')

  if (import.meta.env.DEV) {
    return {
      ...commonConfig,
      environment: 'local',
      debug: true,
      tracePropagationTargets: tracePropagationTargets || ['localhost'],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      tracesSampleRate: 0,
    }
  }

  return {
    ...commonConfig,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.VITE_ENVIRONMENT || 'local',
    debug: import.meta.env.VITE_DEBUG === 'true',
    replaysSessionSampleRate: Number.parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1'),
    replaysOnErrorSampleRate: Number.parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'),
    tracesSampleRate: Number.parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.25'),
    profilesSampleRate: 1.0,
    tracePropagationTargets,
  }
}

const sentryConfig: Partial<BrowserOptions> = getSentryConfig()

export default sentryConfig
